import type { JSX } from 'react';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { CardHeader, CardV2 as Card, ConfirmModal } from '@feathr/components';

import AnhingaEnvProfileRow from './AnhingaEnvProfileRow';
import type { TAnhingaEnvProfile } from './AnhingaEnvSwitcher.hooks';
import useAnhingaEnvSwitcher, { ANHINGA_ENV_PROFILES } from './AnhingaEnvSwitcher.hooks';

interface IAnhingaEnvSwitcherProps {
  /** The backend version payload from useBlackboxVersion. */
  version:
    | {
        DB_ENV: string;
        ES_ENV: string;
        FLASK_ENV: string;
      }
    | undefined;
}

/**
 * Local-dev-only card on the Version page that switches the anhinga backend
 * between environment profiles by rewriting its .env and recreating docker.
 * Switching to production points the local app at the production DB/ES, so it
 * is gated behind a confirmation.
 */
function AnhingaEnvSwitcher({ version }: Readonly<IAnhingaEnvSwitcherProps>): JSX.Element {
  const { t } = useTranslation();
  const { currentProfile, isSwitching, switchingTo, handleSwitch } = useAnhingaEnvSwitcher(version);
  const [isConfirmingProduction, setIsConfirmingProduction] = useState(false);

  const labels: Record<TAnhingaEnvProfile, string> = {
    development: t('Development'),
    staging: t('Staging'),
    production: t('Production'),
  };

  function handleSelect(profile: TAnhingaEnvProfile): void {
    if (profile === 'production') {
      setIsConfirmingProduction(true);
      return;
    }
    void handleSwitch(profile);
  }

  function handleCloseConfirm(): void {
    setIsConfirmingProduction(false);
  }

  async function handleConfirmProduction(): Promise<void> {
    setIsConfirmingProduction(false);
    await handleSwitch('production');
  }

  return (
    <Card name={'anhinga-env'}>
      <CardHeader
        description={t('Switch the local backend environment and recreate its Docker services.')}
        title={t('Backend Environment')}
      />
      {ANHINGA_ENV_PROFILES.map((profile) => (
        <AnhingaEnvProfileRow
          isCurrent={currentProfile === profile}
          isSwitching={isSwitching}
          isSwitchingThis={switchingTo === profile}
          key={profile}
          label={labels[profile]}
          onSelect={handleSelect}
          profile={profile}
        />
      ))}
      {isConfirmingProduction && (
        <ConfirmModal
          alertDescription={t(
            'This points your local app at the production database and Elasticsearch. Proceed with care.',
          )}
          alertTitle={t('You are switching to production')}
          confirmButtonText={t('Switch to production')}
          confirmButtonType={'danger'}
          onClose={handleCloseConfirm}
          onConfirm={handleConfirmProduction}
          opened={true}
          t={t}
          title={t('Switch to production?')}
        />
      )}
    </Card>
  );
}

export default AnhingaEnvSwitcher;
